import {
  Card,
  CrudMode,
  DataDisplay,
  formatDate,
  formatDecimal,
  formatPercent,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, Input } from "components";
import { PartnerIndirectCost, PartnerIndirectCostViewResource } from "generated/openApi";

import produce from "immer";
import "moment-timezone";

export const PartnerIndirectCostPageBody = (props: {
  crudMode: CrudMode;
  indirectCost: PartnerIndirectCost;
  setIndirectCost: (value: PartnerIndirectCost) => void;
  viewResource: PartnerIndirectCostViewResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();
  const properties = props.viewResource.properties || [];

  const startDateField = () => {
    const propertyName = pn<PartnerIndirectCost>("startDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.indirectCost.startDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.indirectCost.startDate}
        onChange={(value) =>
          props.setIndirectCost(
            produce(props.indirectCost, (draft) => {
              draft.startDate = value;
            })
          )
        }
      />
    );
  };

  const endDateField = () => {
    const propertyName = pn<PartnerIndirectCost>("endDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.indirectCost.endDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.indirectCost.endDate}
        onChange={(value) =>
          props.setIndirectCost(
            produce(props.indirectCost, (draft) => {
              draft.endDate = value;
            })
          )
        }
      />
    );
  };

  const indirectPercentField = () => {
    const propertyName = pn<PartnerIndirectCost>("indirectPercent");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {formatPercent(props.indirectCost.indirectPercent)}
      </DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.indirectCost.indirectPercent}
        onChange={(e) =>
          props.setIndirectCost(
            produce(props.indirectCost, (draft) => {
              draft.indirectPercent = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const mileageField = () => {
    const propertyName = pn<PartnerIndirectCost>("mileage");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDecimal(props.indirectCost.mileage, 3)}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.indirectCost.mileage}
        onChange={(e) =>
          props.setIndirectCost(
            produce(props.indirectCost, (draft) => {
              draft.mileage = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      <Grid item xs={smallFieldBreakpoints}>
        {startDateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {endDateField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {indirectPercentField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {mileageField()}
      </Grid>
    </Grid>
  );
};
